import { type KeyboardDownCapture } from '@canvas/input-types/keyboard-events-types';

import { type SelectionsMachine } from '../../machine/selection-machine.ts';
import { type SharedEventLoop } from '../event-loop.ts';
import { type SelectionInput } from '../types.ts';

import { isEditableTarget } from './utils/is-editable-target.ts';

/**
 * Handles a keyboard capture after shared keyboard-input filtering has run.
 */
export type KeyboardInputHandler = (
  capture: KeyboardDownCapture,
  machine: SelectionsMachine
) => void;

/**
 * Configures a keyboard input wrapper for one focused behavior family.
 */
export interface KeyboardInputForConfig {
  handler: KeyboardInputHandler;
}

/**
 * Creates a keyboard input that delegates usable keydown captures to one handler.
 *
 * The wrapper keeps common input hygiene in one place: it skips non-keyboard
 * captures, already-consumed captures, and editable DOM targets before the
 * behavior-specific handler sees the event.
 */
export function createKeyboardInputFor(
  machine: SelectionsMachine,
  sharedLoop: SharedEventLoop,
  config: KeyboardInputForConfig
): SelectionInput {
  const { handler } = config;
  let unsubscribe: () => void;

  return {
    setup() {
      unsubscribe = sharedLoop.subscribe((events) => {
        for (const event of events) {
          if (event.type !== 'keyboardDown') {
            continue;
          }

          if (event.properties.consumed) {
            continue;
          }

          if (isEditableTarget(event.properties.event.target)) {
            continue;
          }

          handler(event, machine);
        }
      });
    },

    dispose() {
      unsubscribe?.();
    }
  };
}
